import React, { useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { getUserConfig } from '@/functions';

const defaultContext = Object.freeze({
  /**
   * @type {import('./UserConfigContext').UserConfigState}
   */
  state: 'uninit',
  /**
   * @type {Record<import('./UserConfigContext').UserConfigKey, boolean|string>}
   */
  config: {},
});

/**
 * User config context
 *
 * Provides team & user settings as they pertain to a specific user.
 */
export const UserConfigContext = React.createContext({ ...defaultContext });

// provide protection around tests that mock react.
if (UserConfigContext) {
  UserConfigContext.displayName = 'UserConfigContext';
}

/**
 * The React Context Provider for User Config
 *
 * @param {Object} props
 * @param {string} props.token
 * @param {string} props.environment
 * @param {React.ReactNode} props.children
 * @returns {JSX.Element}
 */
export function UserConfigProvider({ token, environment, children }) {
  const parentContext = useContext(UserConfigContext);
  const [localContext, setLocalContext] = useState(parentContext);
  const [fetchData, setShouldFetchData] = useState(false);

  useEffect(() => {
    if (!fetchData || !token || !environment) {
      return;
    }

    const getConfigData = async () => {
      const result = await getUserConfig(token, environment);
      if (result.ok) {
        setLocalContext({ state: 'ready', config: result.data });
        return;
      }

      setLocalContext({ state: 'error', config: {} });
    };

    getConfigData();
  }, [fetchData, token, environment]);

  // send down the parent context by default (and not our copy of it via nextContext)
  let providerContext = parentContext;

  // unless if the parent context is uninitialized
  if (parentContext.state === 'uninit') {
    // (we'll use our local context instead)
    providerContext = localContext;
    // and if our local context is uninitialized
    if (localContext.state === 'uninit') {
      // then we're the first! so update it to loading and
      // trigger the fetching logic!
      setLocalContext((state) => ({ ...state, state: 'loading' }));
      setShouldFetchData(true);
    }
  }

  return (
    <UserConfigContext.Provider value={providerContext}>{children}</UserConfigContext.Provider>
  );
}

UserConfigProvider.propTypes = {
  token: PropTypes.string.isRequired,
  environment: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};
